import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import FormInput from '../../components/forms/FormInput';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { updateProfile, changePassword } from '../../api/auth';
import { getGasSettings, updateGasSettings, importApartments, getApartments, updateApartment } from '../../api/gasReadings';

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState({ name: user?.name || '', email: user?.email || '' });
  const [passwords, setPasswords] = useState({ current_password: '', password: '', password_confirmation: '' });
  const [profileMsg, setProfileMsg] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Gas Settings State
  const [gasSettings, setGasSettings] = useState({ price_per_kg: '', m3_to_kg_factor: '' });
  const [gasSettingsMsg, setGasSettingsMsg] = useState('');
  const [gasSettingsLoading, setGasSettingsLoading] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [aptTower, setAptTower] = useState('A');
  const [aptList, setAptList] = useState([]);
  const [aptLoading, setAptLoading] = useState(false);
  const [editingApt, setEditingApt] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    getGasSettings().then((res) => {
      if (res.data) setGasSettings(res.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setAptLoading(true);
    getApartments({ tower: aptTower }).then((res) => {
      setAptList(res.data || []);
    }).catch(() => {}).finally(() => setAptLoading(false));
  }, [aptTower]);

  const handleGasSettingsSubmit = async (e) => {
    e.preventDefault();
    setGasSettingsMsg('');
    setGasSettingsLoading(true);
    try {
      const res = await updateGasSettings({
        price_per_kg: parseFloat(gasSettings.price_per_kg),
        m3_to_kg_factor: parseFloat(gasSettings.m3_to_kg_factor),
      });
      setGasSettings(res.data);
      setGasSettingsMsg('Gas settings updated successfully');
    } catch (err) {
      setGasSettingsMsg(err.response?.data?.message || 'Failed to update gas settings');
    } finally {
      setGasSettingsLoading(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportMsg('');
    setImportLoading(true);
    try {
      await importApartments(file);
      setImportMsg('Apartments imported successfully');
      getApartments({ tower: aptTower }).then((res) => setAptList(res.data || []));
    } catch (err) {
      setImportMsg(err.response?.data?.message || 'Failed to import apartments');
    } finally {
      setImportLoading(false);
      e.target.value = '';
    }
  };

  const handleAptSave = async (apt) => {
    try {
      await updateApartment(apt.id, editForm);
      setAptList(aptList.map((a) => a.id === apt.id ? { ...a, ...editForm } : a));
      setEditingApt(null);
    } catch {
      // silent
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileMsg('');
    setProfileLoading(true);
    try {
      const res = await updateProfile(profile);
      updateUser(res.data || res);
      setProfileMsg('Profile updated successfully');
    } catch (err) {
      setProfileMsg(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMsg('');
    setPasswordLoading(true);
    try {
      await changePassword(passwords);
      setPasswords({ current_password: '', password: '', password_confirmation: '' });
      setPasswordMsg('Password changed successfully');
    } catch (err) {
      setPasswordMsg(err.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <AppLayout title="Profile" breadcrumbs="Profile">
      <div className="max-w-2xl space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h2>
          {profileMsg && <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm mb-4">{profileMsg}</div>}
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <FormInput label="Name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} required />
            <FormInput label="Email" type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} required />
            <Button type="submit" loading={profileLoading}>Save Changes</Button>
          </form>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h2>
          {passwordMsg && <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm mb-4">{passwordMsg}</div>}
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <FormInput label="Current Password" type="password" value={passwords.current_password}
              onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} required />
            <FormInput label="New Password" type="password" value={passwords.password}
              onChange={(e) => setPasswords({ ...passwords, password: e.target.value })} required />
            <FormInput label="Confirm New Password" type="password" value={passwords.password_confirmation}
              onChange={(e) => setPasswords({ ...passwords, password_confirmation: e.target.value })} required />
            <Button type="submit" loading={passwordLoading}>Change Password</Button>
          </form>
        </div>
        {/* Gas Settings — hidden for gas_reader */}
        {user?.role !== 'gas_reader' && <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Gas Reading Settings</h2>
          {gasSettingsMsg && <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm mb-4">{gasSettingsMsg}</div>}
          <form onSubmit={handleGasSettingsSubmit} className="space-y-4">
            <FormInput
              label="Price per KG (₹)"
              type="number"
              step="0.01"
              value={gasSettings.price_per_kg}
              onChange={(e) => setGasSettings({ ...gasSettings, price_per_kg: e.target.value })}
              required
            />
            <FormInput
              label="Cubic Meter to KG Factor"
              type="number"
              step="0.001"
              value={gasSettings.m3_to_kg_factor}
              onChange={(e) => setGasSettings({ ...gasSettings, m3_to_kg_factor: e.target.value })}
              required
            />
            <Button type="submit" loading={gasSettingsLoading}>Save Gas Settings</Button>
          </form>
        </div>}

        {/* Apartment Management — hidden for gas_reader */}
        {user?.role !== 'gas_reader' && <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Apartment Management</h2>

          <div className="flex items-center gap-3 mb-4">
            <label className="relative cursor-pointer">
              <span className="inline-flex items-center px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">
                {importLoading ? 'Importing...' : 'Upload Excel'}
              </span>
              <input type="file" accept=".xlsx,.csv" onChange={handleImport} className="hidden" disabled={importLoading} />
            </label>
            {importMsg && <span className="text-sm text-blue-700">{importMsg}</span>}
          </div>

          <div className="flex gap-2 mb-4">
            {['A', 'B'].map((t) => (
              <button
                key={t}
                onClick={() => setAptTower(t)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  aptTower === t ? 'bg-primary-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Tower {t}
              </button>
            ))}
          </div>

          {aptLoading ? (
            <p className="text-sm text-gray-500">Loading apartments...</p>
          ) : (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Unit</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Owner Name</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Phone</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {aptList.map((apt) => (
                    <tr key={apt.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">{apt.unit}</td>
                      <td className="px-3 py-2">
                        {editingApt === apt.id ? (
                          <input
                            className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                            value={editForm.owner_name ?? ''}
                            onChange={(e) => setEditForm({ ...editForm, owner_name: e.target.value })}
                          />
                        ) : (
                          apt.owner_name || '—'
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editingApt === apt.id ? (
                          <input
                            className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                            value={editForm.owner_phone ?? ''}
                            onChange={(e) => setEditForm({ ...editForm, owner_phone: e.target.value })}
                          />
                        ) : (
                          apt.owner_phone || '—'
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editingApt === apt.id ? (
                          <div className="flex gap-1">
                            <button onClick={() => handleAptSave(apt)} className="text-xs text-green-600 hover:underline">Save</button>
                            <button onClick={() => setEditingApt(null)} className="text-xs text-gray-500 hover:underline">Cancel</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setEditingApt(apt.id); setEditForm({ owner_name: apt.owner_name || '', owner_phone: apt.owner_phone || '' }); }}
                            className="text-xs text-primary-700 hover:underline"
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>}
      </div>
    </AppLayout>
  );
}
